import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import InputText from '@/components/UI/InputText/InputText';
import { getTitleWithoutDoormanText, putDoormanTextTitle } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import { getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import campaignServices from '@/services/campaignServices';
import { CampaignInfo } from '@/types';
import { ThemeOptions } from '@/utils/componentStyles';
import styles from '../DialogNewCR/DialogNewCR.module.scss';

interface DialogEditTitleProps {
  close: () => void;
  headerLanguage: string;
  editCR: CampaignInfo;
}

const useStyles = makeStyles(({ cartTheme }: ThemeOptions) => ({
  titleRoot: {
    borderBottom: `1px solid ${cartTheme.commonGrey}`,
  },
  buttonsRoot: {
    borderTop: `1px solid ${cartTheme.commonGrey}`,
  },
  dialogPaper: {
    minWidth: '30%',
  },
}));

const DialogEditTitle: React.FC<DialogEditTitleProps> = (props) => {
  const { close, headerLanguage, editCR } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const [state, setState] = useState({
    loading: false,
    requestTitle: editCR?.title
      ? getTitleWithoutDoormanText(editCR.title?.[headerLanguage], headerLanguage, editCR?.doormanSelected)
      : '',
    isDoorman: editCR?.doormanSelected,
    titleError: [false, false],
  });

  const cantSave = state.loading || !state.requestTitle || state.titleError[0] || state.titleError[1];

  const handleStateChange = (prop: string, newValue) => {
    if (prop === 'requestTitle') {
      let newTitle: string = newValue;

      if (newValue.trim().length > 0) {
        newTitle = newValue.replace(/^\s+/g, '');
      }
      const errorMinLength = newTitle.trim().length < 1;
      const errorMaxLength = newTitle.length > 240;

      return setState({ ...state, [prop]: newTitle, titleError: [errorMinLength, errorMaxLength] });
    }

    const newState = {
      ...state,
      [prop]: newValue,
    };

    setState(newState);
  };

  const editCampaign = () => {
    handleStateChange('loading', true);

    const newTitle = {
      title: {
        ...editCR?.title,
        [headerLanguage]: putDoormanTextTitle(state.requestTitle.trim(), headerLanguage, state.isDoorman),
      },
    };
    campaignServices.updateCampaignTitle(editCR?.id, newTitle)
      .then(close)
      .catch((err) => addToast(err?.message, 'ERROR'))
      .finally(() => handleStateChange('loading', false));
  };

  return (
    <Dialog
      open
      id="editCR-dialog"
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
      onClose={close}
      aria-labelledby="editCR-dialog-title"
      disableEscapeKeyDown={state.loading}
    >
      <DialogTitle classes={{ root: classes.titleRoot }} id="editCR-dialog-title">
        {t('editX', { x: editCR.id })}
      </DialogTitle>
      <DialogContent>
        <InputText
          id="request-title"
          label={t('dialog.requestTitle')}
          handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleStateChange('requestTitle', ev.target.value)}
          value={state.requestTitle}
          formControlClass={styles.RequestTitle}
          startAdornment={state.isDoorman
            ? `${t('campaignRequests.doormanFixedTitle', { lng: headerLanguage.toLowerCase() })}:`
            : null}
          helperText={[
            ...((state.titleError[0]) ? [t('validations.crTitleMinLength')] : []),
            ...((state.titleError[1]) ? [t('validations.crTitleMaxLength', { maxLength: 240 })] : []),
          ]}
          isMandatory
        />
      </DialogContent>
      <DialogActions classes={{ root: classes.buttonsRoot }}>
        <Button id="create-update-btn" disabled={cantSave} variant="contained" color="primary" onClick={editCampaign}>
          {t('dialog.updateTitle')}
        </Button>
        <Button id="back-cancel-btn" disabled={state.loading} color="primary" onClick={close}>
          {t('dialog.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
});

export default connect(mapStateToProps)(DialogEditTitle);
